import { css } from "@emotion/react";
import React, { useState } from "react";
import { Button, Flex, Heading, Loading, Text } from "@phoenix/components";

export interface YamlConfigMetadata {
  id: string;
  name: string;
  description?: string | null;
  project_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface YamlConfigRevisionMetadata {
  id: string;
  config_id: string;
  revision_number: number;
  name?: string | null;
  description?: string | null;
  change_summary?: string | null;
  checksum?: string | null;
  parent_revision_id?: string | null;
  created_at?: string | null;
}

export interface YamlConfigRevisionDetail {
  id: string;
  config_id: string;
  revision_number: number;
  name?: string | null;
  description?: string | null;
  change_summary?: string | null;
  yaml_content: string;
  checksum?: string | null;
  parent_revision_id?: string | null;
  created_at?: string | null;
}

export interface YamlConfigDetail {
  id: string;
  name: string;
  description?: string | null;
  yaml_content: string;
  project_id?: string | null;
  current_revision_id?: string | null;
  current_revision_number?: number | null;
  created_at: string;
  updated_at: string;
}

const modalOverlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const modalContentStyle = css`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const modalHeaderStyle = css`
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
`;

const modalBodyStyle = css`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 480px;
  overflow-y: auto;
`;

const modalFooterStyle = css`
  padding: 14px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background-color: #f8fafc;
`;

const inputLabelStyle = css`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
`;

const textInputStyle = css`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.2);
  }
`;

const textareaStyle = css`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 72px;
  resize: vertical;
  outline: none;
  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.2);
  }
`;

const configItemCardStyle = (isActive: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${isActive ? "#0f766e" : "#e2e8f0"};
  background-color: ${isActive ? "#f0fdf4" : "#ffffff"};
  transition: all 0.15s ease-in-out;
  &:hover {
    border-color: #0f766e;
    background-color: #f8fafc;
  }
`;

// ---------------------------------------------------------------------------
// 1. Save / Save As Dialog
// ---------------------------------------------------------------------------

export interface SaveConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => Promise<void>;
  initialName?: string;
  initialDescription?: string;
  isSaveAs?: boolean;
}

export function SaveConfigModal({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  initialDescription = "",
  isSaveAs = false,
}: SaveConfigModalProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  React.useEffect(() => {
    setName(initialName);
    setDescription(initialDescription);
    setErrorMessage(null);
  }, [initialName, initialDescription, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMessage("Configuration name is required.");
      return;
    }
    setErrorMessage(null);
    setIsSaving(true);
    try {
      await onSave(name.trim(), description.trim());
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save configuration.";
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div css={modalOverlayStyle}>
      <div css={modalContentStyle}>
        <form onSubmit={handleSubmit}>
          <div css={modalHeaderStyle}>
            <Heading level={5} style={{ margin: 0, color: "#0f172a" }}>
              {isSaveAs ? "Save As New Configuration" : "Save Configuration"}
            </Heading>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: "18px",
                color: "#64748b",
              }}
            >
              ✕
            </button>
          </div>

          <div css={modalBodyStyle}>
            {errorMessage && (
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  fontSize: "13px",
                }}
              >
                {errorMessage}
              </div>
            )}

            <div>
              <div css={inputLabelStyle}>
                Configuration Name <span style={{ color: "#e11d48" }}>*</span>
              </div>
              <input
                type="text"
                css={textInputStyle}
                placeholder="e.g. Production Support Bot Scan"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                disabled={isSaving}
              />
            </div>

            <div>
              <div css={inputLabelStyle}>Description (Optional)</div>
              <textarea
                css={textareaStyle}
                placeholder="Optional notes or intended scope of this test configuration..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          <div css={modalFooterStyle}>
            <Button variant="default" size="S" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="S"
              type="submit"
              isDisabled={isSaving || !name.trim()}
              style={{ backgroundColor: "#0f766e", color: "#ffffff" }}
            >
              {isSaving ? (
                <Flex direction="row" gap="size-50" alignItems="center">
                  <Loading size="S" />
                  <span>Saving...</span>
                </Flex>
              ) : (
                "Save Configuration"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Load Saved Configurations Modal
// ---------------------------------------------------------------------------

export interface LoadConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  configs: YamlConfigMetadata[];
  activeConfigId?: string | null;
  onSelectConfig: (configId: string) => Promise<void>;
  onDeleteConfig: (configId: string, name: string) => Promise<void>;
  onViewHistory?: (configId: string, name: string) => void;
  isLoading?: boolean;
}

export function LoadConfigModal({
  isOpen,
  onClose,
  configs,
  activeConfigId,
  onSelectConfig,
  onDeleteConfig,
  onViewHistory,
  isLoading = false,
}: LoadConfigModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingConfigId, setLoadingConfigId] = useState<string | null>(null);
  const [deletingConfigId, setDeletingConfigId] = useState<string | null>(null);

  if (!isOpen) return null;

  const filtered = configs.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.description && c.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = async (id: string) => {
    setLoadingConfigId(id);
    try {
      await onSelectConfig(id);
      onClose();
    } finally {
      setLoadingConfigId(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the configuration "${name}"?`)) {
      return;
    }
    setDeletingConfigId(id);
    try {
      await onDeleteConfig(id, name);
    } finally {
      setDeletingConfigId(null);
    }
  };

  return (
    <div css={modalOverlayStyle}>
      <div css={modalContentStyle} style={{ maxWidth: "620px" }}>
        <div css={modalHeaderStyle}>
          <Heading level={5} style={{ margin: 0, color: "#0f172a" }}>
            Saved Red Team Configurations ({configs.length})
          </Heading>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: "18px",
              color: "#64748b",
            }}
          >
            ✕
          </button>
        </div>

        <div css={modalBodyStyle}>
          {configs.length > 5 && (
            <input
              type="text"
              css={textInputStyle}
              placeholder="Search saved configurations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}

          {isLoading ? (
            <div style={{ padding: "40px 0" }}>
              <Flex direction="column" gap="size-100" alignItems="center">
                <Loading size="M" />
                <Text size="S">Loading configurations...</Text>
              </Flex>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 16px", color: "#64748b" }}>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
                {searchTerm ? "No matching configurations found." : "No saved configurations yet."}
              </p>
              <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#94a3b8" }}>
                Save your current configuration from the YAML editor to reuse it anytime.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {filtered.map((item) => {
                const isActive = item.id === activeConfigId;
                const isItemLoading = loadingConfigId === item.id;
                const isItemDeleting = deletingConfigId === item.id;

                return (
                  <div key={item.id} css={configItemCardStyle(isActive)}>
                    <div style={{ minWidth: 0, flex: 1, marginRight: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontWeight: 650, fontSize: "14px", color: "#0f172a" }}>
                          {item.name}
                        </span>
                        {isActive && (
                          <span
                            style={{
                              fontSize: "11px",
                              backgroundColor: "#bbf7d0",
                              color: "#166534",
                              padding: "1px 6px",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            Active
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                          {item.description}
                        </p>
                      )}
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                        Updated: {new Date(item.updated_at).toLocaleString()}
                      </span>
                    </div>

                    <Flex direction="row" gap="size-50" alignItems="center">
                      {onViewHistory && (
                        <Button
                          variant="default"
                          size="S"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewHistory(item.id, item.name);
                          }}
                          isDisabled={isItemLoading || isItemDeleting}
                          title={`View revision history for ${item.name}`}
                        >
                          History
                        </Button>
                      )}
                      <Button
                        variant="default"
                        size="S"
                        onClick={(e) => handleDelete(e, item.id, item.name)}
                        isDisabled={isItemDeleting || isItemLoading}
                        style={{ color: "#e11d48" }}
                      >
                        {isItemDeleting ? <Loading size="S" /> : "Delete"}
                      </Button>
                      <Button
                        variant="primary"
                        size="S"
                        onClick={() => handleSelect(item.id)}
                        isDisabled={isItemLoading || isItemDeleting}
                        style={{ backgroundColor: "#0f766e", color: "#ffffff" }}
                      >
                        {isItemLoading ? <Loading size="S" /> : "Load"}
                      </Button>
                    </Flex>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div css={modalFooterStyle}>
          <Button variant="default" size="S" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Unsaved Changes Confirmation Modal
// ---------------------------------------------------------------------------

export interface UnsavedChangesModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirmDiscard: () => void;
  message?: string;
}

export function UnsavedChangesModal({
  isOpen,
  onCancel,
  onConfirmDiscard,
  message = "You have unsaved changes in the YAML configuration. If you proceed, your current edits will be discarded.",
}: UnsavedChangesModalProps) {
  if (!isOpen) return null;

  return (
    <div css={modalOverlayStyle}>
      <div css={modalContentStyle} style={{ maxWidth: "460px" }}>
        <div css={modalHeaderStyle}>
          <Heading level={5} style={{ margin: 0, color: "#991b1b" }}>
            Unsaved Changes
          </Heading>
          <button
            type="button"
            onClick={onCancel}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: "18px",
              color: "#64748b",
            }}
          >
            ✕
          </button>
        </div>

        <div css={modalBodyStyle}>
          <Text size="M">{message}</Text>
        </div>

        <div css={modalFooterStyle}>
          <Button variant="default" size="S" onClick={onCancel}>
            Keep Editing
          </Button>
          <Button
            variant="default"
            size="S"
            onClick={onConfirmDiscard}
            style={{ backgroundColor: "#e11d48", color: "#ffffff", border: "none" }}
          >
            Discard Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
